import type { ReactNode } from "react";
import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const p9Faqs: LearnFAQ[] = [
  {
    question: "Can TMS help anxiety?",
    answer:
      "TMS is FDA-cleared for anxiety symptoms in adults who have major depressive disorder, on the BrainsWay (2021) and NeuroStar (2022) systems. In that group, anxiety typically improves alongside depression. TMS is not FDA-cleared for generalized anxiety disorder, panic disorder, or social anxiety on their own; small studies exist but there is no pivotal trial. If you have anxiety without depression, psychotherapy and medication remain the evidence-based first-line treatments, and any TMS would be off-label.",
  },
  {
    question: "Is TMS FDA-cleared for OCD?",
    answer:
      "Yes, for one device. BrainsWay Deep TMS with the H7 coil was granted FDA marketing authorization for obsessive-compulsive disorder through the De Novo pathway in 2018, based on a multicenter sham-controlled trial in adults whose OCD had not responded adequately to prior treatment. Sessions target the medial prefrontal and anterior cingulate cortices and include brief symptom provocation. Other TMS devices are not cleared for OCD unless their labeling says so.",
  },
  {
    question: "Does TMS work for PTSD?",
    answer:
      "It is unproven. Several small randomized trials, including research within the Department of Veterans Affairs, have tested TMS for PTSD with different targets and frequencies; some showed benefit over sham and others did not. No TMS device is FDA-cleared for PTSD, so any use is off-label and usually not covered by insurance. Trauma-focused psychotherapies such as EMDR, prolonged exposure, and cognitive processing therapy remain the first-line treatments.",
  },
  {
    question: "Can TMS be used for bipolar depression?",
    answer:
      "Only off-label, and with caution. Randomized trials of TMS for the depressive phase of bipolar disorder have had mixed results and small samples, and no device is FDA-cleared for bipolar disorder. TMS carries a small risk of triggering mania or hypomania, so it should not be given during a manic episode and, if used at all, requires a mood stabilizer and close monitoring. First-line treatments are mood-stabilizing medications and, for severe episodes, ECT.",
  },
  {
    question: 'What does "off-label" TMS mean?',
    answer:
      "Off-label means a clinician uses an FDA-cleared TMS device for a condition the device is not cleared to treat, such as PTSD or bipolar depression. This is legal, and physicians may do it when they judge the evidence and risks acceptable, but the manufacturer cannot promote that use, the supporting evidence is typically weaker or mixed, and insurance rarely pays. You should be told explicitly when a proposed treatment is off-label and give informed consent.",
  },
  {
    question: "Is magnetic therapy for anxiety the same as TMS?",
    answer:
      "No. Consumer magnetic therapy products such as bracelets, mats, or static magnets are not TMS and have no evidence for anxiety or depression. TMS uses a powerful, rapidly changing electromagnetic field generated by a medical device, operated by trained staff under a physician's supervision, to induce electrical currents in specific brain regions. Only prescription TMS systems are FDA-cleared, and only for the indications on their labeling.",
  },
  {
    question: "Can TMS treat eating disorders?",
    answer:
      "Not as an established treatment. Studies of TMS for anorexia nervosa, bulimia nervosa, and binge eating disorder are small and early-stage, with inconsistent findings, and no device is FDA-cleared for any eating disorder. Marketing that suggests otherwise is not supported by evidence. Eating disorders are best treated by specialized programs combining nutritional rehabilitation, psychotherapy, and medical monitoring; TMS should be considered only within a research study.",
  },
  {
    question: "How does TMS treat depression and anxiety at the same time?",
    answer:
      "In adults with major depression, standard TMS protocols stimulate the left dorsolateral prefrontal cortex, a region involved in regulating both mood and worry. Trials measuring anxiety scales in depressed patients found anxiety symptoms improved along with depression, which supported the anxious-depression clearances. The same course of about 30 to 36 sessions addresses both; there is no separate anxiety protocol. Because the evidence comes from people with depression, this does not establish that TMS treats anxiety disorders in people without depression.",
  },
];

export const page9: LearnPageData = {
  slug: "tms-for-anxiety-ocd-ptsd",
  title: "TMS for Anxiety, OCD, PTSD and Bipolar: FDA Status",
  description:
    "Condition-by-condition guide to TMS for anxiety, OCD, PTSD, and bipolar depression: which uses are FDA-cleared, which are off-label or investigational, and why.",
  canonical: "/learn/tms-for-anxiety-ocd-ptsd",
  h1: "Can TMS Help With Anxiety, OCD, PTSD, or Bipolar Depression?",
  breadcrumbLabel: "TMS for Anxiety, OCD, PTSD",
  crumbs: baseCrumbs("TMS for Anxiety, OCD, PTSD"),
  shortAnswer:
    "TMS is FDA-cleared for anxiety symptoms in adults with major depression and for OCD (on the BrainsWay H7 coil). TMS for PTSD and bipolar depression is off-label and not FDA-cleared. Call Neuro Wellness TMS Centers of America at 954-827-9793 to discuss what is appropriate for your situation.",
  keyTakeaways: [
    "TMS is FDA-cleared for anxiety symptoms in adults with MDD (BrainsWay 2021, NeuroStar 2022) — not for anxiety disorders alone.",
    "TMS is FDA-cleared for OCD on the BrainsWay H7 coil (De Novo, 2018).",
    "TMS for PTSD is off-label and unproven; no device is FDA-cleared for PTSD.",
    "TMS for bipolar depression is off-label and carries a small risk of triggering mania.",
    "Off-label use is legal but evidence is weaker and insurance rarely pays.",
  ],
  sections: [
    {
      heading: "TMS for anxiety",
      body: (
        <>
          {P(
            "TMS is FDA-cleared for anxiety symptoms in adults who have major depressive disorder, on the BrainsWay (2021) and NeuroStar (2022) systems. In that population, anxiety typically improves alongside depression, because the stimulated region (the left dorsolateral prefrontal cortex) is involved in regulating both mood and worry.",
          )}
          {P(
            "TMS is not FDA-cleared for generalized anxiety disorder, panic disorder, or social anxiety on their own. Small studies exist, but there is no pivotal trial. If you have anxiety without depression, psychotherapy and medication remain the evidence-based first-line treatments, and any TMS would be off-label.",
          )}
        </>
      ),
    },
    {
      heading: "TMS for OCD",
      body: (
        <>
          {P(
            "TMS is FDA-cleared for obsessive-compulsive disorder on one device: BrainsWay Deep TMS with the H7 coil, granted FDA marketing authorization through the De Novo pathway in 2018. The clearance was based on a multicenter sham-controlled trial in adults whose OCD had not responded adequately to prior treatment. Sessions target the medial prefrontal and anterior cingulate cortices and include brief symptom provocation.",
          )}
          {P(
            "Other TMS devices are not cleared for OCD unless their labeling says so. If a clinic offers TMS for OCD, ask which device and coil it uses and whether that device is FDA-cleared for OCD.",
          )}
        </>
      ),
    },
    {
      heading: "TMS for PTSD",
      body: (
        <>
          {P(
            "TMS for PTSD is unproven. Several small randomized trials, including research within the Department of Veterans Affairs, have tested TMS for PTSD with different targets and frequencies; some showed benefit over sham and others did not. No TMS device is FDA-cleared for PTSD, so any use is off-label and usually not covered by insurance.",
          )}
          {P(
            "Trauma-focused psychotherapies — EMDR, prolonged exposure, and cognitive processing therapy — remain the first-line treatments for PTSD. Neuro Wellness TMS Centers of America also offers Prism, an EEG-based neurofeedback approach for PTSD.",
          )}
        </>
      ),
    },
    {
      heading: "TMS for bipolar depression",
      body: (
        <>
          {P(
            "TMS for the depressive phase of bipolar disorder is off-label. Randomized trials have had mixed results and small samples, and no device is FDA-cleared for bipolar disorder. TMS carries a small risk of triggering mania or hypomania, so it should not be given during a manic episode and, if used at all, requires a mood stabilizer and close monitoring.",
          )}
          {P(
            "First-line treatments for bipolar depression are mood-stabilizing medications and, for severe episodes, ECT.",
          )}
        </>
      ),
    },
    {
      heading: "Regulatory status: what off-label means",
      body: (
        <div className="my-6 rounded-xl border border-medium-blue/30 bg-medium-blue/5 p-5">
          <p>
            <strong>Regulatory status.</strong> TMS devices are FDA-cleared for
            specific indications. Using a cleared device for a condition it is
            not cleared to treat (such as PTSD or bipolar depression) is
            off-label: legal, but the evidence is typically weaker or mixed and
            insurance rarely pays. You should be told explicitly when a proposed
            treatment is off-label and give informed consent. No provider should
            imply that off-label TMS is FDA-cleared for that condition.
          </p>
        </div>
      ),
    },
  ],
  faqs: p9Faqs,
  sources: [
    {
      citation:
        "Carmi L, et al. Efficacy and safety of deep transcranial magnetic stimulation for obsessive-compulsive disorder: A prospective multicenter randomized double-blind placebo-controlled trial. Am J Psychiatry. 2019;176(11):931-938.",
    },
    {
      citation:
        "FDA De Novo authorization, BrainsWay Deep TMS H7 coil for OCD (2018).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/denovo.cfm",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/tms-for-anxiety-ocd-ptsd#webpage`,
        name: "Can TMS Help With Anxiety, OCD, PTSD, or Bipolar Depression?",
        url: `${SITE_URL}/learn/tms-for-anxiety-ocd-ptsd`,
        description:
          "Condition-by-condition guide to TMS for anxiety, OCD, PTSD, and bipolar depression: which uses are FDA-cleared, which are off-label or investigational, and why.",
        about: [
          {
            "@type": "MedicalCondition",
            name: "Major depressive disorder",
            code: {
              "@type": "MedicalCode",
              code: "D003865",
              codingSystem: "MeSH",
            },
          },
          {
            "@type": "MedicalCondition",
            name: "Obsessive-compulsive disorder",
            code: {
              "@type": "MedicalCode",
              code: "D009771",
              codingSystem: "MeSH",
            },
          },
          {
            "@type": "MedicalCondition",
            name: "Post-traumatic stress disorder",
            code: {
              "@type": "MedicalCode",
              code: "D013313",
              codingSystem: "MeSH",
            },
          },
          {
            "@type": "MedicalCondition",
            name: "Bipolar disorder",
            code: {
              "@type": "MedicalCode",
              code: "D001714",
              codingSystem: "MeSH",
            },
          },
          {
            "@type": "MedicalCondition",
            name: "Anxiety disorders",
            code: {
              "@type": "MedicalCode",
              code: "D001008",
              codingSystem: "MeSH",
            },
          },
        ],
      }),
      orgNode,
      {
        "@type": "MedicalTherapy",
        name: "Repetitive transcranial magnetic stimulation (rTMS)",
        alternateName: "TMS therapy",
        procedureType: "Noninvasive",
        relevantSpecialty: { "@type": "MedicalSpecialty", name: "Psychiatry" },
      },
      faqJsonld(p9Faqs, `${SITE_URL}/learn/tms-for-anxiety-ocd-ptsd#faq`),
      breadcrumbJsonld(baseCrumbs("TMS for Anxiety, OCD, PTSD")),
    ],
  },
  relatedLinks: sharedRelated,
};
